<script setup>
import { computed, ref, watch } from "vue";
import svc from "@/services/super-purchase.service";
import { toast } from "@/utils/toast";

const props = defineProps({
  transaction: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["reviewed"]);

const reviewNotes = ref("");
const reviewing = ref(false);

watch(
  () => props.transaction,
  (value) => {
    reviewNotes.value = value?.review_notes || "";
  },
  { immediate: true }
);

const invoiceNo = computed(() => props.transaction?.invoice_no || "-");
const companyName = computed(() => props.transaction?.company_name || "Unknown Company");
const companyEmail = computed(() => props.transaction?.company_email || "-");
const amountDisplay = computed(() => props.transaction?.amount_display || "-");
const paymentMethod = computed(() => props.transaction?.payment_method || "-");
const paymentStatus = computed(() => props.transaction?.payment_status || "-");
const createdDate = computed(() => props.transaction?.created_date || "-");
const paidAt = computed(() => props.transaction?.paid_at || "-");
const reference = computed(() => props.transaction?.transaction_reference || "-");
const planName = computed(() => props.transaction?.plan_name || "-");
const proofName = computed(() => props.transaction?.proof_file_name || "Payment proof");
const proofUploadedAt = computed(() => props.transaction?.proof_uploaded_at || "-");
const reviewedBy = computed(() => props.transaction?.reviewed_by_name || "-");
const reviewedAt = computed(() => props.transaction?.reviewed_at || "-");
const hasProof = computed(() => !!props.transaction?.has_proof);

async function reviewTransaction(status) {
  if (!props.transaction?.id) return;
  reviewing.value = true;
  try {
    const res = await svc.review(props.transaction.id, {
      payment_status: status,
      review_notes: reviewNotes.value || null,
    });
    toast(res?.data?.message || "Transaction review saved.");
    emit("reviewed", res?.data?.transaction || null);
  } catch (error) {
    toast(error?.response?.data?.message || "Failed to save transaction review.", "error");
  } finally {
    reviewing.value = false;
  }
}

function openProof() {
  if (!props.transaction?.id) return;
  svc.downloadProof(props.transaction.id)
    .then((res) => {
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = proofName.value;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      toast(error?.response?.data?.message || "Failed to download payment proof.", "error");
    });
}
</script>

<template>
  <div class="modal fade" id="view_invoice">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-body p-5">
          <div class="row justify-content-between align-items-center mb-4">
            <div class="col-md-7">
              <h5 class="text-dark mb-1">Purchase Transaction</h5>
              <p class="mb-1"><i class="ti ti-file-invoice me-1"></i>{{ invoiceNo }}</p>
              <p class="mb-0"><i class="ti ti-calendar me-1"></i>Created: {{ createdDate }}</p>
            </div>
            <div class="col-md-5 text-md-end mt-3 mt-md-0">
              <h4 class="mb-1">{{ amountDisplay }}</h4>
              <span class="badge bg-primary text-capitalize">{{ paymentStatus }}</span>
            </div>
          </div>

          <div class="table-responsive mb-3">
            <table class="table">
              <tbody>
                <tr>
                  <th>Company</th>
                  <td>{{ companyName }}</td>
                </tr>
                <tr>
                  <th>Email</th>
                  <td>{{ companyEmail }}</td>
                </tr>
                <tr>
                  <th>Plan</th>
                  <td>{{ planName }}</td>
                </tr>
                <tr>
                  <th>Payment Method</th>
                  <td>{{ paymentMethod }}</td>
                </tr>
                <tr>
                  <th>Paid At</th>
                  <td>{{ paidAt }}</td>
                </tr>
                <tr>
                  <th>Reference</th>
                  <td>{{ reference }}</td>
                </tr>
                <tr>
                  <th>Proof</th>
                  <td>
                    <div v-if="hasProof" class="d-flex flex-wrap align-items-center gap-2">
                      <span>{{ proofName }}</span>
                      <button type="button" class="btn btn-sm btn-primary" @click="openProof">
                        <i class="ti ti-download me-1"></i>Open Proof
                      </button>
                      <span class="text-muted">Uploaded: {{ proofUploadedAt }}</span>
                    </div>
                    <span v-else class="text-muted">No proof uploaded</span>
                  </td>
                </tr>
                <tr>
                  <th>Reviewed By</th>
                  <td>{{ reviewedBy }}</td>
                </tr>
                <tr>
                  <th>Reviewed At</th>
                  <td>{{ reviewedAt }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="border rounded-3 p-3 mb-4">
            <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h6 class="mb-0">Review Payment</h6>
              <span class="text-muted small">Use this for bank transfer and other manual reviews.</span>
            </div>
            <textarea
              v-model="reviewNotes"
              class="form-control mb-3"
              rows="3"
              placeholder="Optional review notes"
            ></textarea>
            <div class="d-flex flex-wrap gap-2">
              <button type="button" class="btn btn-success" :disabled="reviewing" @click="reviewTransaction('paid')">
                <i class="ti ti-circle-check me-1"></i>Mark Paid
              </button>
              <button type="button" class="btn btn-warning" :disabled="reviewing" @click="reviewTransaction('pending')">
                <i class="ti ti-clock me-1"></i>Keep Pending
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="reviewing" @click="reviewTransaction('unpaid')">
                Mark Unpaid
              </button>
              <button type="button" class="btn btn-outline-danger" :disabled="reviewing" @click="reviewTransaction('failed')">
                Mark Failed
              </button>
              <button type="button" class="btn btn-outline-danger" :disabled="reviewing" @click="reviewTransaction('refunded')">
                Mark Refunded
              </button>
            </div>
          </div>

          <div class="text-end">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
